import React, { useState, useMemo, useCallback, useRef } from 'react';
import { func, arrayOf, number } from 'prop-types';
import { compact } from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { useI18n } from '@bingads-webui-campaign-react/smart-page-context';

import { ProfessionalServiceForm } from './professional-service-form';
import { professionalServicePropTypes } from '../consts/prop-types';

const UnstyledAddProfessionalServiceDialog = ({
  onAdd,
  onCancel,
  editingIndex,
  services,
}) => {
  const i18n = useI18n();

  const editing = editingIndex > -1;
  const servicesRef = useRef(services);
  const [formData, setFormData] = useState(services[editingIndex]);
  const [isValid, setIsValid] = useState(false);

  const dialogContentProps = useMemo(() => ({
    type: DialogType.close,
    title: i18n.getString(editing ? _TL_('Update a service') : _TL_('Add a new service offered')),
    closeButtonAriaLabel: i18n.getString(_TL_('Close')),
  }), [i18n, editing]);

  const onFormValidate = useCallback((res) => {
    setFormData(res.data);
    setIsValid(res.isValid);
  }, []);

  const onFormAdd = useCallback(() => {
    const { name } = formData;
    onAdd({ ...formData, name: name.trim() });
  }, [formData, onAdd]);

  const existingServiceNames = useMemo(() => {
    servicesRef.current[editingIndex] = undefined;
    return compact(servicesRef.current).map(s => s.name.toLowerCase());
  }, [editingIndex, servicesRef]);

  return (
    <Dialog
      className="smart-page-add-professional-service-dialog"
      hidden={false}
      onDismiss={onCancel}
      dialogContentProps={dialogContentProps}
      minWidth={613}
      modalProps={{ isBlocking: true }}
    >
      <ProfessionalServiceForm
        onValidate={onFormValidate}
        existingServiceNames={existingServiceNames}
        defaultValue={formData}
      />
      <DialogFooter>
        <PrimaryButton
          text={i18n.getString(editing ? _TL_('Save') : _TL_('Add'))}
          disabled={!isValid}
          onClick={onFormAdd}
        />
        <DefaultButton
          text={i18n.getString(_TL_('Cancel'))}
          onClick={onCancel}
        />
      </DialogFooter>
    </Dialog>
  );
};

UnstyledAddProfessionalServiceDialog.propTypes = {
  onAdd: func.isRequired,
  onCancel: func.isRequired,
  editingIndex: number,
  services: arrayOf(professionalServicePropTypes),
};

UnstyledAddProfessionalServiceDialog.defaultProps = {
  editingIndex: -1,
  services: [],
};

export const AddProfessionalServiceDialog = withDefaultStyles(
  UnstyledAddProfessionalServiceDialog,
  () => ({})
);
